import { useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { getValidAuthHeadersOrThrow } from '../lib/auth';
import { getAdminId } from '../utils/getAdminId';
import { useAuth } from '../contexts/AuthContext';
import { preprocessAddress } from '../utils/addressPreprocessor';
import {
  buildSparseAddressDiff,
  hasSignificantIssues,
} from '../utils/aiAddressLogPayload';

export interface AiAddressInput {
  name?: string;
  street?: string;
  houseNumber?: string;
  houseNumberExt?: string;
  addressExtra?: string;
  postalCode?: string;
  city?: string;
  countryCode?: string;
  phone?: string;
  region?: string;
}

export interface RawShopifyAddress {
  address1?: string;
  address2?: string;
  formatted?: string[];
  name?: string;
  company?: string;
  city?: string;
  zip?: string;
  province?: string;
  provinceCode?: string;
  country?: string;
  countryCodeV2?: string;
  phone?: string;
}

export interface AiNormalizedAddress {
  name: string;
  street: string;
  houseNumber: string;
  houseNumberExt: string;
  addressExtra: string;
  postalCode: string;
  city: string;
  countryCode: string;
  phone: string;
  region: string;
}

export interface AiIssue {
  field: string;
  severity: 'error' | 'warning' | 'info';
  message: string;
}

export interface AiDiagnostics {
  confidence: string;
  wasModified: boolean;
  originalLanguage: string;
  issues: AiIssue[];
}

export interface AiNormalizeResult {
  normalized: AiNormalizedAddress;
  diagnostics: AiDiagnostics;
}

interface AiSettings {
  enabled: boolean;
  hasKey: boolean;
}

export const useAiAddress = () => {
  const { user } = useAuth();
  const [processing, setProcessing] = useState(false);
  const [lastResult, setLastResult] = useState<AiNormalizeResult | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  const settingsCache = useRef<AiSettings | null>(null);
  const dedupCache = useRef<Map<string, AiNormalizeResult>>(new Map());
  const prevAdminIdRef = useRef<string | null>(null);

  const adminId = getAdminId(user);

  if (adminId !== prevAdminIdRef.current) {
    prevAdminIdRef.current = adminId;
    settingsCache.current = null;
    dedupCache.current.clear();
  }

  const checkAiEnabled = useCallback(async (): Promise<AiSettings> => {
    if (settingsCache.current) return settingsCache.current;
    if (!adminId) return { enabled: false, hasKey: false };

    try {
      const { data } = await supabase
        .from('users')
        .select('ai_address_enabled')
        .eq('id', adminId)
        .maybeSingle();

      const settings: AiSettings = {
        enabled: data?.ai_address_enabled === true,
        hasKey: data?.ai_address_enabled === true,
      };
      settingsCache.current = settings;
      return settings;
    } catch {
      return { enabled: false, hasKey: false };
    }
  }, [adminId]);

  const invalidateCache = useCallback(() => {
    settingsCache.current = null;
  }, []);

  const logModification = useCallback((
    original: AiAddressInput,
    result: AiNormalizeResult,
    orderContext?: { shopifyOrderId?: number | bigint; orderNumber?: string; source?: string },
  ) => {
    if (!adminId) return;

    const sparse = buildSparseAddressDiff(original, result.normalized);
    const hasDiff = Object.keys(sparse.original_address).length > 0;
    const sigIssues = hasSignificantIssues(result.diagnostics.issues);

    // Only persist field-level fixes or error/warning diagnostics (not info-only, not unchanged).
    if (!hasDiff && !sigIssues) return;

    const issuesForLog = (result.diagnostics.issues || []).filter(
      (i) => i.severity === 'error' || i.severity === 'warning',
    );

    supabase.from('ai_address_log').insert({
      admin_id: adminId,
      shopify_order_id: orderContext?.shopifyOrderId ?? null,
      order_number: orderContext?.orderNumber ?? null,
      country_code: result.normalized.countryCode || original.countryCode || '',
      original_address: sparse.original_address,
      corrected_address: sparse.corrected_address,
      diagnostics: {
        ...result.diagnostics,
        issues: issuesForLog,
      },
      source: orderContext?.source ?? null,
    }).then(({ error: logErr }) => {
      if (logErr) console.warn('[useAiAddress] Failed to log AI modification:', logErr.message);
    });
  }, [adminId]);

  const normalizeAddress = useCallback(async (
    address: AiAddressInput,
    rawShopify?: RawShopifyAddress,
    orderContext?: { shopifyOrderId?: number | bigint; orderNumber?: string; source?: string },
  ): Promise<AiNormalizeResult | null> => {
    setAiError(null);
    setLastResult(null);

    const settings = await checkAiEnabled();
    if (!settings.enabled || !settings.hasKey) return null;

    // Dedup cache: skip if we already processed this exact address
    const cacheKey = JSON.stringify(address);
    const cached = dedupCache.current.get(cacheKey);
    if (cached) {
      setLastResult(cached);
      return cached;
    }

    // Local pre-processing: clean address before sending to AI
    const { address: preprocessed, changes } = preprocessAddress(address);

    setProcessing(true);
    try {
      const aiAddress = changes.length > 0 ? preprocessed : address;
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-normalize-address`;
      const response = await fetch(url, {
        method: 'POST',
        headers: await getValidAuthHeadersOrThrow(),
        body: JSON.stringify({ address: aiAddress, rawShopifyAddress: rawShopify }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        const errMsg = data.error || 'AI normalization failed';
        // Surface the reason (via the existing error box) instead of silently
        // disabling AI for the whole session. A later order still retries.
        setAiError(errMsg);
        return null;
      }

      const result: AiNormalizeResult = {
        normalized: data.normalized,
        diagnostics: data.diagnostics,
      };

      dedupCache.current.set(cacheKey, result);
      setLastResult(result);
      logModification(address, result, orderContext);

      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'AI normalization failed';
      setAiError(msg);
      return null;
    } finally {
      setProcessing(false);
    }
  }, [checkAiEnabled, logModification]);

  return {
    processing,
    lastResult,
    aiError,
    normalizeAddress,
    checkAiEnabled,
    invalidateCache,
    clearResult: useCallback(() => { setLastResult(null); setAiError(null); }, []),
  };
};
